import { fork, put, all, call, takeLatest } from "redux-saga/effects";

import {
  getBudgetRecordsSuccess,
  getBudgetRecordsFailure,
  getResourceConfigsSuccess,
  getResourceConfigsFailure,
  createBudgetRecordSuccess,
  createBudgetRecordFailure,
  updateBudgetRecordSuccess,
  updateBudgetRecordFailure,
  deleteBudgetRecordSuccess,
  deleteBudgetRecordFailure,
  getOrgBudgetsSuccess,
  getOrgBudgetsFailure,
  createOrgBudgetSuccess,
  createOrgBudgetFailure,
  updateOrgBudgetSuccess,
  updateOrgBudgetFailure,
  deleteOrgBudgetSuccess,
  deleteOrgBudgetFailure,
  getBudgetChartsSuccess,
  getBudgetChartsFailure,
  getBudgetHistorySuccess,
  getBudgetHistoryFailure,
  getOrgBudgetHistorySuccess,
  getOrgBudgetHistoryFailure,
  getOrgBudgetAlertsSuccess,
  getOrgBudgetAlertsFailure,
  createOrgBudgetAlertSuccess,
  createOrgBudgetAlertFailure,
  updateOrgBudgetAlertSuccess,
  updateOrgBudgetAlertFailure,
  deleteOrgBudgetAlertSuccess,
  deleteOrgBudgetAlertFailure,
  getOrgBudgetScheduledReports,
  getOrgBudgetScheduledReportsSuccess,
  getOrgBudgetScheduledReportsFailure,
  createOrgBudgetScheduledReport,
  createOrgBudgetScheduledReportSuccess,
  createOrgBudgetScheduledReportFailure,
  updateOrgBudgetScheduledReport,
  updateOrgBudgetScheduledReportSuccess,
  updateOrgBudgetScheduledReportFailure,
  deleteOrgBudgetScheduledReport,
  deleteOrgBudgetScheduledReportSuccess,
  deleteOrgBudgetScheduledReportFailure,
  getBudgetGroupAlertsSuccess,
  getBudgetGroupAlertsFailure,
  createBudgetGroupAlertSuccess,
  createBudgetGroupAlertFailure,
  updateBudgetGroupAlertSuccess,
  updateBudgetGroupAlertFailure,
  deleteBudgetGroupAlertSuccess,
  deleteBudgetGroupAlertFailure,
  getBudgetGroupScheduledReports,
  getBudgetGroupScheduledReportsSuccess,
  getBudgetGroupScheduledReportsFailure,
  createBudgetGroupScheduledReport,
  createBudgetGroupScheduledReportSuccess,
  createBudgetGroupScheduledReportFailure,
  updateBudgetGroupScheduledReport,
  updateBudgetGroupScheduledReportSuccess,
  updateBudgetGroupScheduledReportFailure,
  deleteBudgetGroupScheduledReport,
  deleteBudgetGroupScheduledReportSuccess,
  deleteBudgetGroupScheduledReportFailure,
} from "./budgetSlice";

import {
  getBudgetRecords as getBudgetRecordsApi,
  getBudgetResourceConfigs as getBudgetResourceConfigsApi,
  createBudgetRecord as createBudgetRecordApi,
  updateBudgetRecord as updateBudgetRecordApi,
  deleteBudgetRecord as deleteBudgetRecordApi,
  getOrgBudgets as getOrgBudgetsApi,
  createOrgBudget as createOrgBudgetApi,
  updateOrgBudget as updateOrgBudgetApi,
  deleteOrgBudget as deleteOrgBudgetApi,
  getBudgetCharts as getBudgetChartsApi,
  getBudgetHistory as getBudgetHistoryApi,
  getOrgBudgetHistory as getOrgBudgetHistoryApi,
  getOrgBudgetAlerts as getOrgBudgetAlertsApi,
  createOrgBudgetAlert as createOrgBudgetAlertApi,
  updateOrgBudgetAlert as updateOrgBudgetAlertApi,
  deleteOrgBudgetAlert as deleteOrgBudgetAlertApi,
  getOrgBudgetScheduledReports as getOrgBudgetScheduledReportsApi,
  createOrgBudgetScheduledReport as createOrgBudgetScheduledReportApi,
  updateOrgBudgetScheduledReport as updateOrgBudgetScheduledReportApi,
  deleteOrgBudgetScheduledReport as deleteOrgBudgetScheduledReportApi,
  getBudgetGroupAlerts as getBudgetGroupAlertsApi,
  createBudgetGroupAlert as createBudgetGroupAlertApi,
  updateBudgetGroupAlert as updateBudgetGroupAlertApi,
  deleteBudgetGroupAlert as deleteBudgetGroupAlertApi,
  getBudgetGroupScheduledReports as getBudgetGroupScheduledReportsApi,
  createBudgetGroupScheduledReport as createBudgetGroupScheduledReportApi,
  updateBudgetGroupScheduledReport as updateBudgetGroupScheduledReportApi,
  deleteBudgetGroupScheduledReport as deleteBudgetGroupScheduledReportApi,
} from "../../helpers/auth_aws_helper";

// 🛠️ Utility function to handle error responses consistently
const handleApiError = (error, operation) => {
  console.error(`Saga: ${operation} Error =`, error);
  
  if (error.response) {
    // Server responded with error status
    const errorData = error.response.data;
    
    // Handle different error response formats from API Gateway
    if (errorData) {
      // Check for error key first (API Gateway format)
      if (errorData.error && typeof errorData.error === "string") {
        return errorData.error;
      }
      
      // Check for message key
      if (errorData.message && typeof errorData.message === "string") {
        return errorData.message;
      }
      
      // Handle AWS error objects
      if (errorData.error && typeof errorData.error === "object") {
        const awsError = errorData.error;
        
        // Handle AWS SQS errors
        if (awsError.includes("AWS.SimpleQueueService.NonExistentQueue")) {
          return "Queue service error: The specified queue does not exist or you do not have access to it.";
        }
        if (awsError.includes("AWS.SimpleQueueService")) {
          return "Queue service error: " + awsError.split("when calling")[0].trim();
        }
        
        // Handle other AWS service errors
        if (awsError.includes("AWS.")) {
          const serviceError = awsError.split("when calling")[0].trim();
          return "AWS service error: " + serviceError;
        }
        
        // Return the full AWS error message
        return awsError;
      }
      
      // Handle string error data
      if (typeof errorData === "string") {
        return errorData;
      }
      
      // Handle object error data - try to extract meaningful message
      if (typeof errorData === "object") {
        // Try to find any string property that might contain the error message
        for (const [key, value] of Object.entries(errorData)) {
          if (typeof value === "string" && value.length > 0) {
            return value;
          }
        }
      }
    }
    
    return `Server error: ${error.response.status}`;
  } else if (error.request) {
    // Request was made but no response received (timeout, network error, etc.)
    if (error.message && error.message.includes("timeout")) {
      return "Request timed out. Please check your connection and try again.";
    }
    return "No response received from server. Please check your connection.";
  } else {
    // Something else happened
    return error.message || "An unexpected error occurred.";
  }
};

// 🔄 Worker: Fetch Budget Records
function* fetchBudgetRecords() {
  try {
    console.log("Saga: Calling getBudgetRecords");
    const response = yield call(getBudgetRecordsApi);
    console.log("Saga: Budget Records Response =", response);

    if (response.status === 200) {
      console.log("Saga: Full response object =", response);
      console.log("Saga: response.data =", response.data);
      console.log("Saga: response.status =", response.status);
      
      // Handle potential string response that gets spread into object
      let budgetRecords;
      
             // First, check if response.data is undefined - this might be the case when API returns empty array
       if (response.data === undefined) {
         console.log("Saga: response.data is undefined, checking if response itself is an array");
         // If response.data is undefined, check if the response itself is an array
         if (Array.isArray(response)) {
           budgetRecords = response;
         } else {
           // If response is an object with status, try to extract data from response
           const { status, ...rest } = response;
           if (Array.isArray(rest)) {
             budgetRecords = rest;
           } else {
             // If rest is an object, check if it has numeric keys (spread string)
             const keys = Object.keys(rest);
             const isStringObject = keys.every(key => !isNaN(key)) && 
                                   keys.length > 0 && 
                                   keys.some(key => parseInt(key) === 0);
             
             if (isStringObject) {
               // Check if the values are objects (not strings)
               const firstValue = Object.values(rest)[0];
               if (typeof firstValue === 'object' && firstValue !== null) {
                 // This is an array of objects spread into numeric keys
                 budgetRecords = Object.values(rest);
               } else {
                 // This is a spread string, try to reconstruct
                 try {
                   const reconstructedString = Object.values(rest).join('');
                   budgetRecords = JSON.parse(reconstructedString);
                 } catch (parseError) {
                   console.error("Saga: Failed to reconstruct budget records string:", parseError);
                   yield put(getBudgetRecordsFailure("Invalid response format"));
                   return;
                 }
               }
             } else {
               // Regular object, extract the array
               budgetRecords = Object.values(rest);
             }
           }
         }
      } else if (typeof response.data === 'string') {
        // If it's a string, try to parse it as JSON
        try {
          budgetRecords = JSON.parse(response.data);
        } catch (parseError) {
          console.error("Saga: Failed to parse budget records string:", parseError);
          yield put(getBudgetRecordsFailure("Invalid response format"));
          return;
        }
      } else if (Array.isArray(response.data)) {
        // If it's already an array, use it directly
        budgetRecords = response.data;
      } else if (typeof response.data === 'object') {
        // If it's an object, check if it has numeric keys (spread string)
        const keys = Object.keys(response.data);
        const isStringObject = keys.every(key => !isNaN(key)) && 
                              keys.length > 0 && 
                              keys.some(key => parseInt(key) === 0);
        
        if (isStringObject) {
          // This is a spread string, try to reconstruct
          try {
            const reconstructedString = Object.values(response.data).join('');
            budgetRecords = JSON.parse(reconstructedString);
          } catch (parseError) {
            console.error("Saga: Failed to reconstruct budget records string:", parseError);
            yield put(getBudgetRecordsFailure("Invalid response format"));
            return;
          }
        } else {
          // Regular object, extract the array
          budgetRecords = Object.values(response.data);
        }
      } else {
        // Handle other cases
        budgetRecords = response.data;
      }
      
      // Ensure budgetRecords is always an array, even if empty
      if (!Array.isArray(budgetRecords)) {
        console.error("Saga: budgetRecords is not an array:", budgetRecords);
        yield put(getBudgetRecordsFailure("Invalid response format - expected array"));
        return;
      }
      
      console.log("Saga: Final budget records =", budgetRecords);
      yield put(getBudgetRecordsSuccess(budgetRecords));
    } else {
      // Handle non-200 status codes
      let errorMessage = `Unexpected response: ${response.status}`;
      
      if (response.data) {
        // Check for error key first (API Gateway format)
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        } else if (typeof response.data === "object") {
          // Try to find any string property that might contain the error message
          for (const [key, value] of Object.entries(response.data)) {
            if (typeof value === "string" && value.length > 0) {
              errorMessage = value;
              break;
            }
          }
        }
      }
      
      yield put(getBudgetRecordsFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Fetch Budget Records");
    yield put(getBudgetRecordsFailure(errorMessage));
  }
}

// 🔄 Worker: Fetch Resource Configs
function* fetchResourceConfigs() {
  try {
    console.log("Saga: Calling getBudgetResourceConfigs");
    const response = yield call(getBudgetResourceConfigsApi);
    console.log("Saga: Resource Configs Response =", response);

    if (response.status === 200) {
      console.log("Saga: Resource configs data =", response.data);
      
      // Handle potential string response that gets spread into object
      let resourceConfigs;
      
      // First, check if response.data is undefined
      if (response.data === undefined) {
        console.log("Saga: response.data is undefined, checking if response itself is an object");
        // If response.data is undefined, check if the response itself is an object
        if (typeof response === 'object' && !Array.isArray(response)) {
          const { status, ...rest } = response;
          resourceConfigs = rest;
        } else {
          resourceConfigs = response;
        }
      } else if (typeof response.data === 'string') {
        // If it's a string, try to parse it as JSON
        try {
          resourceConfigs = JSON.parse(response.data);
        } catch (parseError) {
          console.error("Saga: Failed to parse resource configs string:", parseError);
          yield put(getResourceConfigsFailure("Invalid response format"));
          return;
        }
      } else if (typeof response.data === 'object') {
        // If it's already an object, use it directly
        resourceConfigs = response.data;
      } else {
        // Handle other cases
        resourceConfigs = response.data;
      }
      
      // Ensure resourceConfigs is always an object
      if (typeof resourceConfigs !== 'object' || resourceConfigs === null) {
        console.error("Saga: resourceConfigs is not an object:", resourceConfigs);
        yield put(getResourceConfigsFailure("Invalid response format - expected object"));
        return;
      }
      
      console.log("Saga: Final resource configs =", resourceConfigs);
      yield put(getResourceConfigsSuccess(resourceConfigs));
    } else {
      // Handle non-200 status codes
      let errorMessage = `Unexpected response: ${response.status}`;
      
      if (response.data) {
        // Check for error key first (API Gateway format)
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        } else if (typeof response.data === "object") {
          // Try to find any string property that might contain the error message
          for (const [key, value] of Object.entries(response.data)) {
            if (typeof value === "string" && value.length > 0) {
              errorMessage = value;
              break;
            }
          }
        }
      }
      
      yield put(getResourceConfigsFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Fetch Resource Configs");
    yield put(getResourceConfigsFailure(errorMessage));
  }
}

// 📄 Worker: Create Budget Record
function* createBudgetRecordSaga({ payload }) {
  try {
    console.log("Saga: Calling createBudgetRecord with payload =", payload);
    const response = yield call(createBudgetRecordApi, payload);
    console.log("Saga: Create Budget Record Response =", response);

    if (response.status === 200 || response.status === 201) {
      // Remove status from response
      const { status, ...rest } = response;

      // Handle different response structures
      let successMessage = "Budget record created successfully";
      
      if (rest.message && typeof rest.message === "string") {
        // Direct message property
        successMessage = rest.message;
      } else if (rest.data && rest.data.message && typeof rest.data.message === "string") {
        // Nested message property
        successMessage = rest.data.message;
      } else if (typeof rest === "string") {
        // Direct string response - check if it's JSON
        try {
          const parsed = JSON.parse(rest);
          if (parsed.message && typeof parsed.message === "string") {
            successMessage = parsed.message;
          } else {
            successMessage = rest;
          }
        } catch (parseError) {
          // Not JSON, use as is
          successMessage = rest;
        }
      } else if (typeof rest.data === "string") {
        // String in data property - check if it's JSON
        try {
          const parsed = JSON.parse(rest.data);
          if (parsed.message && typeof parsed.message === "string") {
            successMessage = parsed.message;
          } else {
            successMessage = rest.data;
          }
        } catch (parseError) {
          // Not JSON, use as is
          successMessage = rest.data;
        }
      } else if (Object.keys(rest).every((key) => !isNaN(key)) && Object.values(rest).every((val) => typeof val === "string")) {
        // Spread string object (legacy format) - check if it's JSON
        const joinedString = Object.values(rest).join("");
        try {
          const parsed = JSON.parse(joinedString);
          if (parsed.message && typeof parsed.message === "string") {
            successMessage = parsed.message;
          } else {
            successMessage = joinedString;
          }
        } catch (parseError) {
          // Not JSON, use as is
          successMessage = joinedString;
        }
      }

      const successData = { message: successMessage };
      yield put(createBudgetRecordSuccess(successData));
      // Automatically refresh budget records and resource configs after successful creation
      yield put({ type: "budget/getBudgetRecords" });
      yield put({ type: "budget/getResourceConfigs" });
    } else {
      // Handle non-200 status codes
      let errorMessage = `Unexpected response: ${response.status}`;
      
      if (response.data) {
        // Check for error key first (API Gateway format)
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        } else if (typeof response.data === "object") {
          // Try to find any string property that might contain the error message
          for (const [key, value] of Object.entries(response.data)) {
            if (typeof value === "string" && value.length > 0) {
              errorMessage = value;
              break;
            }
          }
        }
      }
      
      yield put(createBudgetRecordFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Create Budget Record");
    yield put(createBudgetRecordFailure(errorMessage));
  }
}

// 📝 Worker: Update Budget Record
function* updateBudgetRecordSaga({ payload }) {
  try {
    console.log("Saga: Calling updateBudgetRecord with payload =", payload);
    const response = yield call(updateBudgetRecordApi, payload);
    console.log("Saga: Update Budget Record Response =", response);

    if (response.status === 200) {
      // Remove status from response
      const { status, ...rest } = response;

      // Handle different response structures
      let successMessage = "Budget record updated successfully";
      
      if (rest.message && typeof rest.message === "string") {
        // Direct message property
        successMessage = rest.message;
      } else if (rest.data && rest.data.message && typeof rest.data.message === "string") {
        // Nested message property
        successMessage = rest.data.message;
      } else if (typeof rest === "string") {
        // Direct string response - check if it's JSON
        try {
          const parsed = JSON.parse(rest);
          if (parsed.message && typeof parsed.message === "string") {
            successMessage = parsed.message;
          } else {
            successMessage = rest;
          }
        } catch (parseError) {
          // Not JSON, use as is
          successMessage = rest;
        }
      } else if (typeof rest.data === "string") {
        // String in data property - check if it's JSON
        try {
          const parsed = JSON.parse(rest.data);
          if (parsed.message && typeof parsed.message === "string") {
            successMessage = parsed.message;
          } else {
            successMessage = rest.data;
          }
        } catch (parseError) {
          // Not JSON, use as is
          successMessage = rest.data;
        }
      } else if (Object.keys(rest).every((key) => !isNaN(key)) && Object.values(rest).every((val) => typeof val === "string")) {
        // Spread string object (legacy format) - check if it's JSON
        const joinedString = Object.values(rest).join("");
        try {
          const parsed = JSON.parse(joinedString);
          if (parsed.message && typeof parsed.message === "string") {
            successMessage = parsed.message;
          } else {
            successMessage = joinedString;
          }
        } catch (parseError) {
          // Not JSON, use as is
          successMessage = joinedString;
        }
      }

      const successData = { message: successMessage };
      yield put(updateBudgetRecordSuccess(successData));
      // Automatically refresh budget records and resource configs after successful update
      yield put({ type: "budget/getBudgetRecords" });
      yield put({ type: "budget/getResourceConfigs" });
    } else {
      // Handle non-200 status codes
      let errorMessage = `Unexpected response: ${response.status}`;
      
      if (response.data) {
        // Check for error key first (API Gateway format)
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        } else if (typeof response.data === "object") {
          // Try to find any string property that might contain the error message
          for (const [key, value] of Object.entries(response.data)) {
            if (typeof value === "string" && value.length > 0) {
              errorMessage = value;
              break;
            }
          }
        }
      }
      
      yield put(updateBudgetRecordFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Update Budget Record");
    yield put(updateBudgetRecordFailure(errorMessage));
  }
}

// 🗑️ Worker: Delete Budget Record
function* deleteBudgetRecordSaga({ payload: budgetId }) {
  try {
    console.log("Saga: Calling deleteBudgetRecord with budgetId =", budgetId);
    const response = yield call(deleteBudgetRecordApi, budgetId);
    console.log("Saga: Delete Budget Record Response =", response);

    if (response.status === 200) {
      // Remove status from response
      const { status, ...rest } = response;

      // Handle different response structures
      let successMessage = "Budget record deleted successfully";
      
      if (rest.message && typeof rest.message === "string") {
        // Direct message property
        successMessage = rest.message;
      } else if (rest.data && rest.data.message && typeof rest.data.message === "string") {
        // Nested message property
        successMessage = rest.data.message;
      } else if (typeof rest === "string") {
        // Direct string response - check if it's JSON
        try {
          const parsed = JSON.parse(rest);
          if (parsed.message && typeof parsed.message === "string") {
            successMessage = parsed.message;
          } else {
            successMessage = rest;
          }
        } catch (parseError) {
          // Not JSON, use as is
          successMessage = rest;
        }
      } else if (typeof rest.data === "string") {
        // String in data property - check if it's JSON
        try {
          const parsed = JSON.parse(rest.data);
          if (parsed.message && typeof parsed.message === "string") {
            successMessage = parsed.message;
          } else {
            successMessage = rest.data;
          }
        } catch (parseError) {
          // Not JSON, use as is
          successMessage = rest.data;
        }
      } else if (Object.keys(rest).every((key) => !isNaN(key)) && Object.values(rest).every((val) => typeof val === "string")) {
        // Spread string object (legacy format) - check if it's JSON
        const joinedString = Object.values(rest).join("");
        try {
          const parsed = JSON.parse(joinedString);
          if (parsed.message && typeof parsed.message === "string") {
            successMessage = parsed.message;
          } else {
            successMessage = joinedString;
          }
        } catch (parseError) {
          // Not JSON, use as is
          successMessage = joinedString;
        }
      }

      const successData = { message: successMessage };
      yield put(deleteBudgetRecordSuccess(successData));
      // Automatically refresh budget records and resource configs after successful deletion
      yield put({ type: "budget/getBudgetRecords" });
      yield put({ type: "budget/getResourceConfigs" });
    } else {
      // Handle non-200 status codes
      let errorMessage = `Unexpected response: ${response.status}`;
      
      if (response.data) {
        // Check for error key first (API Gateway format)
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        } else if (typeof response.data === "object") {
          // Try to find any string property that might contain the error message
          for (const [key, value] of Object.entries(response.data)) {
            if (typeof value === "string" && value.length > 0) {
              errorMessage = value;
              break;
            }
          }
        }
      }
      
      yield put(deleteBudgetRecordFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Delete Budget Record");
    yield put(deleteBudgetRecordFailure(errorMessage));
  }
}

// 🔄 Worker: Fetch Organization Budgets
function* fetchOrgBudgets() {
  try {
    console.log("Saga: Calling getOrgBudgets");
    const response = yield call(getOrgBudgetsApi);
    console.log("Saga: Organization Budgets Response =", response);

    if (response.status === 200) {
      console.log("Saga: Full response object =", response);
      console.log("Saga: response.data =", response.data);
      console.log("Saga: response.status =", response.status);
      
      // Handle the response structure: {budgets: Array(1), status: 200}
      let orgBudgets;
      
      if (response.data === undefined) {
        // If response.data is undefined, check if the response itself has a budgets property
        if (response.budgets && Array.isArray(response.budgets)) {
          orgBudgets = response.budgets;
        } else {
          orgBudgets = [];
        }
      } else if (typeof response.data === 'string') {
        // If it's a string, try to parse it as JSON
        try {
          const parsed = JSON.parse(response.data);
          orgBudgets = parsed.budgets || parsed || [];
        } catch (parseError) {
          console.error("Saga: Failed to parse response.data as JSON:", parseError);
          orgBudgets = [];
        }
      } else if (Array.isArray(response.data)) {
        // If it's already an array, use it directly
        orgBudgets = response.data;
      } else if (typeof response.data === 'object') {
        // If it's an object, check for budgets property first
        if (response.data.budgets && Array.isArray(response.data.budgets)) {
          orgBudgets = response.data.budgets;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          orgBudgets = response.data.data;
        } else if (Array.isArray(response.data)) {
          orgBudgets = response.data;
        } else {
          // If it's an object but not an array, try to extract array from it
          const keys = Object.keys(response.data);
          if (keys.length > 0 && Array.isArray(response.data[keys[0]])) {
            orgBudgets = response.data[keys[0]];
          } else {
            orgBudgets = [];
          }
        }
      } else {
        orgBudgets = [];
      }

      console.log("Saga: Final orgBudgets =", orgBudgets);
      yield put(getOrgBudgetsSuccess(orgBudgets));
    } else {
      // Handle non-200 status codes
      let errorMessage = `Unexpected response: ${response.status}`;
      
      if (response.data) {
        // Check for error key first (API Gateway format)
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        } else if (typeof response.data === "object") {
          // Try to find any string property that might contain the error message
          for (const [key, value] of Object.entries(response.data)) {
            if (typeof value === "string" && value.length > 0) {
              errorMessage = value;
              break;
            }
          }
        }
      }
      
      yield put(getOrgBudgetsFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Fetch Organization Budgets");
    yield put(getOrgBudgetsFailure(errorMessage));
  }
}

// 📝 Worker: Create Organization Budget
function* createOrgBudgetSaga({ payload }) {
  try {
    console.log("Saga: Calling createOrgBudget with payload =", payload);
    const response = yield call(createOrgBudgetApi, payload);
    console.log("Saga: Create Organization Budget Response =", response);

    if (response.status === 200 || response.status === 201) {
      // Handle success response
      const successData = { message: response.data?.message || "Organization budget created successfully" };
      yield put(createOrgBudgetSuccess(successData));
    } else {
      // Handle non-200 status codes
      let errorMessage = `Unexpected response: ${response.status}`;
      
      if (response.data) {
        // Check for error key first (API Gateway format)
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        } else if (typeof response.data === "object") {
          // Try to find any string property that might contain the error message
          for (const [key, value] of Object.entries(response.data)) {
            if (typeof value === "string" && value.length > 0) {
              errorMessage = value;
              break;
            }
          }
        }
      }
      
      yield put(createOrgBudgetFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Create Organization Budget");
    yield put(createOrgBudgetFailure(errorMessage));
  }
}

// 📝 Worker: Update Organization Budget
function* updateOrgBudgetSaga({ payload }) {
  try {
    console.log("Saga: Calling updateOrgBudget with payload =", payload);
    const { budgetId, ...updateData } = payload;
    const response = yield call(updateOrgBudgetApi, updateData, budgetId);
    console.log("Saga: Update Organization Budget Response =", response);

    if (response.status === 200) {
      // Handle success response
      const successData = { message: response.data?.message || "Organization budget updated successfully" };
      yield put(updateOrgBudgetSuccess(successData));
    } else {
      // Handle non-200 status codes
      let errorMessage = `Unexpected response: ${response.status}`;
      
      if (response.data) {
        // Check for error key first (API Gateway format)
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        } else if (typeof response.data === "object") {
          // Try to find any string property that might contain the error message
          for (const [key, value] of Object.entries(response.data)) {
            if (typeof value === "string" && value.length > 0) {
              errorMessage = value;
              break;
            }
          }
        }
      }
      
      yield put(updateOrgBudgetFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Update Organization Budget");
    yield put(updateOrgBudgetFailure(errorMessage));
  }
}

// 🗑️ Worker: Delete Organization Budget
function* deleteOrgBudgetSaga({ payload: budgetId }) {
  try {
    console.log("Saga: Calling deleteOrgBudget with budgetId =", budgetId);
    const response = yield call(deleteOrgBudgetApi, budgetId);
    console.log("Saga: Delete Organization Budget Response =", response);

    if (response.status === 200) {
      // Handle success response
      const successData = { message: response.data?.message || "Organization budget deleted successfully" };
      yield put(deleteOrgBudgetSuccess(successData));
    } else {
      // Handle non-200 status codes
      let errorMessage = `Unexpected response: ${response.status}`;
      
      if (response.data) {
        // Check for error key first (API Gateway format)
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        } else if (typeof response.data === "object") {
          // Try to find any string property that might contain the error message
          for (const [key, value] of Object.entries(response.data)) {
            if (typeof value === "string" && value.length > 0) {
              errorMessage = value;
              break;
            }
          }
        }
      }
      
      yield put(deleteOrgBudgetFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Delete Organization Budget");
    yield put(deleteOrgBudgetFailure(errorMessage));
  }
}

// 📊 Worker: Fetch Budget Charts
function* fetchBudgetCharts({ payload: budgetId }) {
  try {
    console.log("Saga: Calling getBudgetCharts with budgetId =", budgetId);
    const response = yield call(getBudgetChartsApi, budgetId);
    console.log("Saga: Budget Charts Response =", response);

    if (response.status === 200) {
      // Handle success response - send the entire monthly object from the API response
      const chartsData = response.monthly;
      console.log("Saga: Budget Charts API Response Data =", chartsData);
      
      // Send the entire monthly object to the Redux store
      yield put(getBudgetChartsSuccess(chartsData));
    } else {
      // Handle non-200 status codes
      let errorMessage = `Unexpected response: ${response.status}`;
      
      if (response.data) {
        // Check for error key first (API Gateway format)
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        } else if (typeof response.data === "object") {
          // Try to find any string property that might contain the error message
          for (const [key, value] of Object.entries(response.data)) {
            if (typeof value === "string" && value.length > 0) {
              errorMessage = value;
              break;
            }
          }
        }
      }
      
      yield put(getBudgetChartsFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Fetch Budget Charts");
    yield put(getBudgetChartsFailure(errorMessage));
  }
}

// 📊 Worker: Fetch Budget History
function* fetchBudgetHistory({ payload: budgetId }) {
  try {
    console.log("Saga: Calling getBudgetHistory with budgetId =", budgetId);
    const response = yield call(getBudgetHistoryApi, budgetId);
    console.log("Saga: Budget History Response =", response);

    if (response.status === 200) {
      // Handle success response - the data is directly in the response object
      console.log("Saga: Budget History API Response Data =", response);
      
      // Convert object with numeric keys to array
      let historyData = [];
      if (response && typeof response === 'object') {
        console.log("Saga: All response keys =", Object.keys(response));
        
        // Extract numeric keys and convert to array
        const numericKeys = Object.keys(response)
          .filter(key => {
            const isNumeric = !isNaN(parseInt(key));
            const notStatus = key !== 'status';
            console.log(`Saga: Key '${key}' - isNumeric: ${isNumeric}, notStatus: ${notStatus}`);
            return isNumeric && notStatus;
          })
          .sort((a, b) => parseInt(a) - parseInt(b));
        
        console.log("Saga: Filtered numeric keys =", numericKeys);
        historyData = numericKeys.map(key => response[key]);
      }
      
      console.log("Saga: Converted Budget History Array =", historyData);
      console.log("Saga: Array length =", historyData.length);
      yield put(getBudgetHistorySuccess(historyData));
    } else {
      // Handle non-200 status codes
      let errorMessage = `Unexpected response: ${response.status}`;
      
      if (response.data) {
        // Check for error key first (API Gateway format)
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        } else if (typeof response.data === "object") {
          // Try to find any string property that might contain the error message
          for (const [key, value] of Object.entries(response.data)) {
            if (typeof value === "string" && value.length > 0) {
              errorMessage = value;
              break;
            }
          }
        }
      }
      
      yield put(getBudgetHistoryFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Fetch Budget History");
    yield put(getBudgetHistoryFailure(errorMessage));
  }
}

// 📊 Worker: Fetch Organization Budget History
function* fetchOrgBudgetHistory({ payload: orgBudgetId }) {
  try {
    console.log("Saga: Calling getOrgBudgetHistory with orgBudgetId =", orgBudgetId);
    const response = yield call(getOrgBudgetHistoryApi, orgBudgetId);
    console.log("Saga: Organization Budget History Response =", response);

    if (response.status === 200) {
      // Handle success response - the data is directly in the response object
      console.log("Saga: Organization Budget History API Response Data =", response);
      
      // Convert object with numeric keys to array
      let historyData = [];
      if (response && typeof response === 'object') {
        console.log("Saga: All response keys =", Object.keys(response));
        
        // Extract numeric keys and convert to array
        const numericKeys = Object.keys(response)
          .filter(key => {
            const isNumeric = !isNaN(parseInt(key));
            const notStatus = key !== 'status';
            console.log(`Saga: Key '${key}' - isNumeric: ${isNumeric}, notStatus: ${notStatus}`);
            return isNumeric && notStatus;
          })
          .sort((a, b) => parseInt(a) - parseInt(b));
        
        console.log("Saga: Filtered numeric keys =", numericKeys);
        historyData = numericKeys.map(key => response[key]);
      }
      
      console.log("Saga: Converted Organization Budget History Array =", historyData);
      console.log("Saga: Array length =", historyData.length);
      yield put(getOrgBudgetHistorySuccess(historyData));
    } else {
      // Handle non-200 status codes
      let errorMessage = `Unexpected response: ${response.status}`;
      
      if (response.data) {
        // Check for error key first (API Gateway format)
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        } else if (typeof response.data === "object") {
          // Try to find any string property that might contain the error message
          for (const [key, value] of Object.entries(response.data)) {
            if (typeof value === "string" && value.length > 0) {
              errorMessage = value;
              break;
            }
          }
        }
      }
      
      yield put(getOrgBudgetHistoryFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Fetch Organization Budget History");
    yield put(getOrgBudgetHistoryFailure(errorMessage));
  }
}

// 🔄 Worker: Fetch Organization Budget Alerts
function* fetchOrgBudgetAlerts({ payload: orgBudgetId }) {
  try {
    console.log("Saga: Calling getOrgBudgetAlerts with orgBudgetId =", orgBudgetId);
    const response = yield call(getOrgBudgetAlertsApi, orgBudgetId);
    console.log("Saga: Organization Budget Alerts Response =", response);

    if (response.status === 200) {
      // Handle success response - extract alerts from response
      const alerts = response.data?.alerts || response.alerts || [];
      console.log("Saga: Organization Budget Alerts =", alerts);
      yield put(getOrgBudgetAlertsSuccess(alerts));
    } else {
      // Handle non-200 status codes
      let errorMessage = `Unexpected response: ${response.status}`;
      
      if (response.data) {
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        }
      }
      
      yield put(getOrgBudgetAlertsFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Fetch Organization Budget Alerts");
    yield put(getOrgBudgetAlertsFailure(errorMessage));
  }
}

// 📝 Worker: Create Organization Budget Alert
function* createOrgBudgetAlertSaga({ payload }) {
  try {
    console.log("Saga: Calling createOrgBudgetAlert with payload =", payload);
    const response = yield call(createOrgBudgetAlertApi, payload);
    console.log("Saga: Create Organization Budget Alert Response =", response);

    if (response.status === 200 || response.status === 201) {
      // Handle success response
      const successData = { message: response.data?.message || "Organization budget alert created successfully" };
      yield put(createOrgBudgetAlertSuccess(successData));
    } else {
      // Handle non-200 status codes
      let errorMessage = `Unexpected response: ${response.status}`;
      
      if (response.data) {
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        }
      }
      
      yield put(createOrgBudgetAlertFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Create Organization Budget Alert");
    yield put(createOrgBudgetAlertFailure(errorMessage));
  }
}

// 📝 Worker: Update Organization Budget Alert
function* updateOrgBudgetAlertSaga({ payload }) {
  try {
    console.log("Saga: Calling updateOrgBudgetAlert with payload =", payload);
    const { alertId, ...updateData } = payload;
    const response = yield call(updateOrgBudgetAlertApi, alertId, updateData);
    console.log("Saga: Update Organization Budget Alert Response =", response);

    if (response.status === 200) {
      // Handle success response
      const successData = { message: response.data?.message || "Organization budget alert updated successfully" };
      yield put(updateOrgBudgetAlertSuccess(successData));
    } else {
      // Handle non-200 status codes
      let errorMessage = `Unexpected response: ${response.status}`;
      
      if (response.data) {
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        }
      }
      
      yield put(updateOrgBudgetAlertFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Update Organization Budget Alert");
    yield put(updateOrgBudgetAlertFailure(errorMessage));
  }
}

// 🗑️ Worker: Delete Organization Budget Alert
function* deleteOrgBudgetAlertSaga({ payload: alertId }) {
  try {
    console.log("Saga: Calling deleteOrgBudgetAlert with alertId =", alertId);
    const response = yield call(deleteOrgBudgetAlertApi, alertId);
    console.log("Saga: Delete Organization Budget Alert Response =", response);

    if (response.status === 200) {
      // Handle success response
      const successData = { message: response.data?.message || "Organization budget alert deleted successfully" };
      yield put(deleteOrgBudgetAlertSuccess(successData));
    } else {
      // Handle non-200 status codes
      let errorMessage = `Unexpected response: ${response.status}`;
      
      if (response.data) {
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        }
      }
      
      yield put(deleteOrgBudgetAlertFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Delete Organization Budget Alert");
    yield put(deleteOrgBudgetAlertFailure(errorMessage));
  }
}

// Organization Budget Scheduled Reports Sagas
function* fetchOrgBudgetScheduledReports({ payload: orgBudgetId }) {
  try {
    console.log("Saga: Calling getOrgBudgetScheduledReports with orgBudgetId =", orgBudgetId);
    const response = yield call(getOrgBudgetScheduledReportsApi, orgBudgetId);
    console.log("Saga: Organization Budget Scheduled Reports Response =", response);

    if (response.status === 200) {
      // Handle success response - extract reports from response
      const reports = response.data?.reports || response.reports || [];
      console.log("Saga: Organization Budget Scheduled Reports =", reports);
      yield put(getOrgBudgetScheduledReportsSuccess(reports));
    } else {
      // Handle non-200 status codes
      let errorMessage = `Unexpected response: ${response.status}`;
      
      if (response.data) {
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        }
      }
      
      yield put(getOrgBudgetScheduledReportsFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Fetch Organization Budget Scheduled Reports");
    yield put(getOrgBudgetScheduledReportsFailure(errorMessage));
  }
}

// 📝 Worker: Create Organization Budget Scheduled Report
function* createOrgBudgetScheduledReportSaga({ payload }) {
  try {
    console.log("Saga: Calling createOrgBudgetScheduledReport with payload =", payload);
    const response = yield call(createOrgBudgetScheduledReportApi, payload);
    console.log("Saga: Create Organization Budget Scheduled Report Response =", response);

    if (response.status === 200 || response.status === 201) {
      // Handle success response
      const successData = { message: response.data?.message || "Organization budget scheduled report created successfully" };
      yield put(createOrgBudgetScheduledReportSuccess(successData));
    } else {
      // Handle non-200 status codes
      let errorMessage = `Unexpected response: ${response.status}`;
      
      if (response.data) {
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        }
      }
      
      yield put(createOrgBudgetScheduledReportFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Create Organization Budget Scheduled Report");
    yield put(createOrgBudgetScheduledReportFailure(errorMessage));
  }
}

// 📝 Worker: Update Organization Budget Scheduled Report
function* updateOrgBudgetScheduledReportSaga({ payload }) {
  try {
    console.log("Saga: Calling updateOrgBudgetScheduledReport with payload =", payload);
    const { reportId, ...updateData } = payload;
    const response = yield call(updateOrgBudgetScheduledReportApi, reportId, updateData);
    console.log("Saga: Update Organization Budget Scheduled Report Response =", response);

    if (response.status === 200) {
      // Handle success response
      const successData = { message: response.data?.message || "Organization budget scheduled report updated successfully" };
      yield put(updateOrgBudgetScheduledReportSuccess(successData));
    } else {
      // Handle non-200 status codes
      let errorMessage = `Unexpected response: ${response.status}`;
      
      if (response.data) {
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        }
      }
      
      yield put(updateOrgBudgetScheduledReportFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Update Organization Budget Scheduled Report");
    yield put(updateOrgBudgetScheduledReportFailure(errorMessage));
  }
}

// 🗑️ Worker: Delete Organization Budget Scheduled Report
function* deleteOrgBudgetScheduledReportSaga({ payload: reportId }) {
  try {
    console.log("Saga: Calling deleteOrgBudgetScheduledReport with reportId =", reportId);
    const response = yield call(deleteOrgBudgetScheduledReportApi, reportId);
    console.log("Saga: Delete Organization Budget Scheduled Report Response =", response);

    if (response.status === 200) {
      // Handle success response
      const successData = { message: response.data?.message || "Organization budget scheduled report deleted successfully" };
      yield put(deleteOrgBudgetScheduledReportSuccess(successData));
    } else {
      // Handle non-200 status codes
      let errorMessage = `Unexpected response: ${response.status}`;
      
      if (response.data) {
        if (response.data.error && typeof response.data.error === "string") {
          errorMessage = response.data.error;
        } else if (response.data.message && typeof response.data.message === "string") {
          errorMessage = response.data.message;
        } else if (typeof response.data === "string") {
          errorMessage = response.data;
        }
      }
      
      yield put(deleteOrgBudgetScheduledReportFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Delete Organization Budget Scheduled Report");
    yield put(deleteOrgBudgetScheduledReportFailure(errorMessage));
  }
}

// Budget Group Alerts Sagas
function* fetchBudgetGroupAlerts({ payload: rgBudgetId }) {
  try {
    console.log("Saga: Calling getBudgetGroupAlerts with rgBudgetId =", rgBudgetId);
    const response = yield call(getBudgetGroupAlertsApi, rgBudgetId);
    console.log("Saga: Get Budget Group Alerts Response =", response);

    if (response.status === 200) {
      // Handle success response
      const alerts = response.alerts || response.data?.alerts || [];
      console.log("Saga: Extracted alerts =", alerts);
      yield put(getBudgetGroupAlertsSuccess(alerts));
    } else {
      // Handle error response
      const errorMessage = response.data?.error || "Failed to fetch budget group alerts";
      yield put(getBudgetGroupAlertsFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Get Budget Group Alerts");
    yield put(getBudgetGroupAlertsFailure(errorMessage));
  }
}

function* createBudgetGroupAlertSaga({ payload }) {
  try {
    console.log("Saga: Calling createBudgetGroupAlert with payload =", payload);
    const response = yield call(createBudgetGroupAlertApi, payload);
    console.log("Saga: Create Budget Group Alert Response =", response);

    if (response.status === 201) {
      // Handle success response
      const successData = { 
        alert_id: response.data?.alert_id,
        message: "Budget group alert created successfully" 
      };
      yield put(createBudgetGroupAlertSuccess(successData));
    } else {
      // Handle error response
      const errorMessage = response.data?.error || "Failed to create budget group alert";
      yield put(createBudgetGroupAlertFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Create Budget Group Alert");
    yield put(createBudgetGroupAlertFailure(errorMessage));
  }
}

function* updateBudgetGroupAlertSaga({ payload: { alertId, updateData } }) {
  try {
    console.log("Saga: Calling updateBudgetGroupAlert with alertId =", alertId, "updateData =", updateData);
    const response = yield call(updateBudgetGroupAlertApi, alertId, updateData);
    console.log("Saga: Update Budget Group Alert Response =", response);

    if (response.status === 200) {
      // Handle success response
      const successData = { message: response.data?.message || "Budget group alert updated successfully" };
      yield put(updateBudgetGroupAlertSuccess(successData));
    } else {
      // Handle error response
      const errorMessage = response.data?.error || "Failed to update budget group alert";
      yield put(updateBudgetGroupAlertFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Update Budget Group Alert");
    yield put(updateBudgetGroupAlertFailure(errorMessage));
  }
}

function* deleteBudgetGroupAlertSaga({ payload: alertId }) {
  try {
    console.log("Saga: Calling deleteBudgetGroupAlert with alertId =", alertId);
    const response = yield call(deleteBudgetGroupAlertApi, alertId);
    console.log("Saga: Delete Budget Group Alert Response =", response);

    if (response.status === 200) {
      // Handle success response
      const successData = { message: response.data?.message || "Budget group alert deleted successfully" };
      yield put(deleteBudgetGroupAlertSuccess(successData));
    } else {
      // Handle error response
      const errorMessage = response.data?.error || "Failed to delete budget group alert";
      yield put(deleteBudgetGroupAlertFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Delete Budget Group Alert");
    yield put(deleteBudgetGroupAlertFailure(errorMessage));
  }
}

// Budget Group Scheduled Reports Sagas
function* fetchBudgetGroupScheduledReports({ payload: rgBudgetId }) {
  try {
    console.log("Saga: Calling getBudgetGroupScheduledReports with rgBudgetId =", rgBudgetId);
    const response = yield call(getBudgetGroupScheduledReportsApi, rgBudgetId);
    console.log("Saga: Get Budget Group Scheduled Reports Response =", response);

    if (response.status === 200) {
      const reports = response.reports || response.data?.reports || [];
      yield put(getBudgetGroupScheduledReportsSuccess(reports));
    } else {
      const errorMessage = response.data?.error || "Failed to fetch budget group scheduled reports";
      yield put(getBudgetGroupScheduledReportsFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Get Budget Group Scheduled Reports");
    yield put(getBudgetGroupScheduledReportsFailure(errorMessage));
  }
}

function* createBudgetGroupScheduledReportSaga({ payload }) {
  try {
    console.log("Saga: Calling createBudgetGroupScheduledReport with payload =", payload);
    const response = yield call(createBudgetGroupScheduledReportApi, payload);
    console.log("Saga: Create Budget Group Scheduled Report Response =", response);

    if (response.status === 201) {
      const successData = { 
        report_id: response.data?.report_id || response.report_id,
        message: "Budget group scheduled report created successfully" 
      };
      yield put(createBudgetGroupScheduledReportSuccess(successData));
    } else {
      const errorMessage = response.data?.error || "Failed to create budget group scheduled report";
      yield put(createBudgetGroupScheduledReportFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Create Budget Group Scheduled Report");
    yield put(createBudgetGroupScheduledReportFailure(errorMessage));
  }
}

function* updateBudgetGroupScheduledReportSaga({ payload }) {
  try {
    const { reportId, updateData } = payload;
    console.log("Saga: Calling updateBudgetGroupScheduledReport with reportId =", reportId, "updateData =", updateData);
    const response = yield call(updateBudgetGroupScheduledReportApi, reportId, updateData);
    console.log("Saga: Update Budget Group Scheduled Report Response =", response);

    if (response.status === 200) {
      const successData = { message: response.data?.message || "Budget group scheduled report updated successfully" };
      yield put(updateBudgetGroupScheduledReportSuccess(successData));
    } else {
      const errorMessage = response.data?.error || "Failed to update budget group scheduled report";
      yield put(updateBudgetGroupScheduledReportFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Update Budget Group Scheduled Report");
    yield put(updateBudgetGroupScheduledReportFailure(errorMessage));
  }
}

function* deleteBudgetGroupScheduledReportSaga({ payload: reportId }) {
  try {
    console.log("Saga: Calling deleteBudgetGroupScheduledReport with reportId =", reportId);
    const response = yield call(deleteBudgetGroupScheduledReportApi, reportId);
    console.log("Saga: Delete Budget Group Scheduled Report Response =", response);

    if (response.status === 200) {
      const successData = { message: response.data?.message || "Budget group scheduled report deleted successfully" };
      yield put(deleteBudgetGroupScheduledReportSuccess(successData));
    } else {
      const errorMessage = response.data?.error || "Failed to delete budget group scheduled report";
      yield put(deleteBudgetGroupScheduledReportFailure(errorMessage));
    }
  } catch (error) {
    const errorMessage = handleApiError(error, "Delete Budget Group Scheduled Report");
    yield put(deleteBudgetGroupScheduledReportFailure(errorMessage));
  }
}

// 👂 Watchers
function* watchBudget() {
  yield takeLatest("budget/getBudgetRecords", fetchBudgetRecords);
  yield takeLatest("budget/getResourceConfigs", fetchResourceConfigs);
  yield takeLatest("budget/createBudgetRecord", createBudgetRecordSaga);
  yield takeLatest("budget/updateBudgetRecord", updateBudgetRecordSaga);
  yield takeLatest("budget/deleteBudgetRecord", deleteBudgetRecordSaga);
  yield takeLatest("budget/getOrgBudgets", fetchOrgBudgets);
  yield takeLatest("budget/createOrgBudget", createOrgBudgetSaga);
  yield takeLatest("budget/updateOrgBudget", updateOrgBudgetSaga);
  yield takeLatest("budget/deleteOrgBudget", deleteOrgBudgetSaga);
  yield takeLatest("budget/getBudgetCharts", fetchBudgetCharts);
  yield takeLatest("budget/getBudgetHistory", fetchBudgetHistory);
  yield takeLatest("budget/getOrgBudgetHistory", fetchOrgBudgetHistory);
  yield takeLatest("budget/getOrgBudgetAlerts", fetchOrgBudgetAlerts);
  yield takeLatest("budget/createOrgBudgetAlert", createOrgBudgetAlertSaga);
  yield takeLatest("budget/updateOrgBudgetAlert", updateOrgBudgetAlertSaga);
  yield takeLatest("budget/deleteOrgBudgetAlert", deleteOrgBudgetAlertSaga);

  // Organization Budget Scheduled Reports watchers
  yield takeLatest("budget/getOrgBudgetScheduledReports", fetchOrgBudgetScheduledReports);
  yield takeLatest("budget/createOrgBudgetScheduledReport", createOrgBudgetScheduledReportSaga);
  yield takeLatest("budget/updateOrgBudgetScheduledReport", updateOrgBudgetScheduledReportSaga);
  yield takeLatest("budget/deleteOrgBudgetScheduledReport", deleteOrgBudgetScheduledReportSaga);

  yield takeLatest("budget/getBudgetGroupAlerts", fetchBudgetGroupAlerts);
  yield takeLatest("budget/createBudgetGroupAlert", createBudgetGroupAlertSaga);
  yield takeLatest("budget/updateBudgetGroupAlert", updateBudgetGroupAlertSaga);
  yield takeLatest("budget/deleteBudgetGroupAlert", deleteBudgetGroupAlertSaga);

  // Budget Group Scheduled Reports watchers
  yield takeLatest("budget/getBudgetGroupScheduledReports", fetchBudgetGroupScheduledReports);
  yield takeLatest("budget/createBudgetGroupScheduledReport", createBudgetGroupScheduledReportSaga);
  yield takeLatest("budget/updateBudgetGroupScheduledReport", updateBudgetGroupScheduledReportSaga);
  yield takeLatest("budget/deleteBudgetGroupScheduledReport", deleteBudgetGroupScheduledReportSaga);
}

// 🧠 Root Saga
export function* budgetSaga() {
  yield all([fork(watchBudget)]);
}

export default budgetSaga;
